import { ApiError } from '../../api';
import { ACTIONS } from '../../copy';

/**
 * Something failed, and the screen says so.
 *
 * Three things this deliberately does:
 *
 *  - It renders *our* pt-BR sentence for the error code, never the server's
 *    message. The server writes for an API caller, in English, and sometimes
 *    about internals.
 *  - It shows the correlation id when there is one. That is the single thing
 *    that lets support find the matching traceback on the server, and the
 *    backend puts it on every response for exactly this purpose.
 *  - It offers a retry only when retrying could work. A 403 will be a 403 again;
 *    a dropped connection or a 500 might not be.
 */
export function ErrorState({
  error,
  title = 'Não foi possível carregar',
  onRetry,
  announce = true,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  /** Off only for the samples in the shell's showcase, which are not real errors. */
  announce?: boolean;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const message = apiError?.message ?? 'Algo deu errado. Tente de novo em instantes.';
  const canRetry = onRetry !== undefined && (apiError === null || apiError.isRetryable);

  return (
    <div className="state state--error" {...(announce ? { role: 'alert' as const } : {})}>
      <p className="state__title">{title}</p>
      <p className="state__message">{message}</p>
      {apiError?.correlationId !== undefined && (
        <p className="state__reference">
          Código de referência: <code>{apiError.correlationId}</code>
        </p>
      )}
      {canRetry && (
        <div className="state__action">
          <button type="button" className="button button--secondary" onClick={onRetry}>
            {ACTIONS.retry}
          </button>
        </div>
      )}
    </div>
  );
}
